import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

/**
 * Checks if the current user has an active suspension and blocks access if so.
 * Renders children normally when not suspended.
 */
export const SuspensionNotice = ({ children }: { children: React.ReactNode }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const [suspended, setSuspended] = useState(false);
  const [reason, setReason] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user) {
      setChecked(true);
      return;
    }
    const check = async () => {
      const { data } = await supabase
        .from("suspensions")
        .select("reason")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .maybeSingle();
      if (data) {
        setSuspended(true);
        setReason(data.reason);
      }
      setChecked(true);
    };
    check();
  }, [user?.id]);

  if (!user) return <>{children}</>;

  if (!checked) {
    return (
      <div className="min-h-screen bg-[var(--color-navy)] p-4 pt-24" role="status" aria-label={t("common.loading", "Loading")}>
        <div className="mx-auto max-w-6xl space-y-4">
          <div className="h-16 ec-shimmer-dark" />
          <div className="grid grid-cols-2 gap-4"><div className="h-40 ec-shimmer-dark" /><div className="h-40 ec-shimmer-dark" /></div>
          <span className="sr-only">{t("common.loading", "Loading")}</span>
        </div>
      </div>
    );
  }

  if (suspended) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 sm:p-6" style={{ background: "var(--color-navy)" }}>
        <div className="max-w-md text-center space-y-6">
          <div className="w-20 h-20 mx-auto flex items-center justify-center bg-destructive/20 border border-destructive/30">
            <Ban className="h-10 w-10 text-destructive" />
          </div>
          <h1 className="text-2xl font-bold text-white">{t('suspension.title')}</h1>
          <p className="text-white/60 leading-relaxed">{t('suspension.body')}</p>
          {reason && (
            <div className="bg-white/5 border border-white/10 p-4 text-left">
              <p className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-1">{t('suspension.reason')}</p>
              <p className="text-sm text-white/80">{reason}</p>
            </div>
          )}
          <p className="text-xs text-white/30">{t('suspension.contact')}</p>
          <Button
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            onClick={logout}
          >
            {t('suspension.signOut')}
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
